/**
 * The app frame every Otii tool shares: the moss sidebar with the wordmark,
 * search, grouped navigation divided by lines, an item pinned apart at the
 * bottom (Admin), and the person's card with its menu. On small screens it
 * becomes a top bar with a slide-over menu.
 */
import { Menu as MenuIcon, Search, X, type LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import wordmark from "../assets/otii-wordmark.png";
import { cn } from "../lib/cn";
import { Avatar } from "./avatar";
import { Menu, type MenuItem } from "./overlay";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** A small count or state shown at the right of the item. */
  badge?: ReactNode;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface ShellUser {
  name: string;
  avatarUrl?: string | null;
  roleLabel: string;
}

export function AppShell({
  product,
  tagline,
  groups,
  pinned,
  user,
  userMenu,
  onSearch,
  children,
}: {
  /** The product name beside the wordmark, e.g. "Hub". */
  product: string;
  tagline: string;
  groups: NavGroup[];
  /** Kept apart from the working menu, always at the bottom (Admin). */
  pinned?: NavItem | null;
  user: ShellUser | null;
  userMenu: (MenuItem | "separator")[];
  /** Shows the search button; the app binds ⌘K itself. */
  onSearch?: () => void;
  children: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);

  const link = ({ to, label, icon: Icon, end, badge }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "group flex h-9 items-center gap-3 rounded-lg px-3 text-[14px] font-semibold transition-colors",
          isActive ? "bg-paper/12 text-paper" : "text-paper/65 hover:bg-paper/6 hover:text-paper",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("size-[18px]", isActive ? "text-terra-soft" : "text-paper/55 group-hover:text-paper/80")} />
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {badge !== undefined && badge !== null && <span className="num shrink-0">{badge}</span>}
        </>
      )}
    </NavLink>
  );

  const nav = (
    <nav className="scroll-thin flex flex-1 flex-col overflow-y-auto px-3" aria-label="Main">
      {groups.map((g, i) => (
        <div key={g.label} role="group" aria-label={g.label} className={cn("flex flex-col gap-0.5", i > 0 && "mt-3 border-t border-paper/10 pt-3")}>
          {g.items.map(link)}
        </div>
      ))}
    </nav>
  );

  const sidebar = (
    <div className="flex h-full flex-col bg-moss pt-5 pb-4">
      <div className="mb-6 flex items-center gap-3 px-6">
        <img src={wordmark} alt="get otii" className="h-9 w-auto" />
        <div className="leading-tight">
          <div className="font-display text-[17px] font-semibold text-paper">{product}</div>
          <div className="text-[11px] font-semibold tracking-wide text-paper/50">{tagline}</div>
        </div>
      </div>
      {onSearch && (
        <button
          type="button"
          onClick={onSearch}
          className="mx-3 mb-4 flex h-9 items-center gap-2 rounded-lg border border-paper/10 bg-paper/5 px-3 text-left text-[13px] text-paper/60 transition-colors hover:bg-paper/10 hover:text-paper/85"
        >
          <Search className="size-4" />
          <span className="flex-1">Search</span>
          <span className="rounded border border-paper/15 px-1.5 text-[11px] font-semibold">⌘K</span>
        </button>
      )}
      {nav}
      {pinned && (
        <nav className="mx-3 mt-4 border-t border-paper/10 pt-3" aria-label="Administration">
          {link(pinned)}
        </nav>
      )}
      {user && (
        <div className={cn("mx-3 border-t border-paper/10 pt-3", pinned ? "mt-3" : "mt-4")}>
          <Menu
            align="start"
            trigger={
              <button type="button" className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-paper/6">
                <Avatar name={user.name} src={user.avatarUrl} size={30} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-paper">{user.name}</span>
                  <span className="block truncate text-[11px] text-paper/50">{user.roleLabel}</span>
                </span>
              </button>
            }
            items={userMenu}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen lg:pl-[244px]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[244px] lg:block">{sidebar}</aside>

      {/* small screens: top bar + slide-over nav */}
      <div className="sticky top-0 z-30 flex h-14 items-center gap-3 bg-moss px-4 lg:hidden">
        <button type="button" onClick={() => setNavOpen(true)} className="grid size-9 place-items-center rounded-md text-paper" aria-label="Open navigation">
          <MenuIcon className="size-5" />
        </button>
        <img src={wordmark} alt="get otii" className="h-7 w-auto" />
        <span className="font-display text-base font-semibold text-paper">{product}</span>
        {onSearch && (
          <button type="button" onClick={onSearch} className="ml-auto grid size-9 place-items-center rounded-md text-paper" aria-label="Search">
            <Search className="size-5" />
          </button>
        )}
      </div>
      {navOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-moss/40" onClick={() => setNavOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[260px] animate-slide-in">
            {sidebar}
            <button type="button" onClick={() => setNavOpen(false)} className="absolute top-4 right-3 grid size-8 place-items-center rounded-md text-paper/70" aria-label="Close navigation">
              <X className="size-4" />
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto w-full max-w-[1480px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
    </div>
  );
}
